import moment from 'moment';
import { assign, merge, isNil } from 'lodash';
import 'starfish/SFCsrf';

const withCredentials = (options, credentials) =>
  assign({
    credentials: credentials
  }, options);

const withHeaders = (options) => {
  const header = options.headers || {};
  const overwrite = {
    "Content-Type": header['Content-Type'] || "application/x-www-form-urlencoded; charset=UTF-8",
    "X-Requested-With": "XMLHttpRequest"
  };

 return merge({}, options, {
   headers: assign({}, header, overwrite, SF.csrf.headers())
 });
};

const withOptions = (options) =>
  withHeaders(withCredentials(options, 'same-origin'));

export const fetchAuthenticated = (url, options) => {
  return fetch(url, withOptions(options));
}

/**
 * Uploads a file using fetch. Content-Type cannot be set in this case, as described in 
 * https://muffinman.io/uploading-files-using-fetch-multipart-form-data/
 * @param {String} url - url the file will be uploaded to 
 * @param {Object} options - options for the request
 * @returns {Promise<Response>} upload response
 */
export const upload = (url, options) => {
  const headers = assign({}, (options.headers || {}), {
    "X-Requested-With": "XMLHttpRequest"
  }, SF.csrf.headers());
  
  return fetch(url, merge({}, withCredentials(options, 'same-origin'), { headers: headers }));
};

export const responseJSON = (httpResponse) => httpResponse.json();

/**
 * Returns a copy of the given parameter map with parameters whose values
 * return true for [isParameterValueEmpty] removed.
 *
 * If [isParameterValueEmpty] is null, returns the input parameter map
 * unmodified.
 *
 * If [paramMap] is falsy, returns an empty object.
 *
 * Examples:
 *
 *     noNil({x: null, y: 33, z: 0}) => {y: 33, z: 0}
 *     noNil({x: null, y: 33, z: 0}, x => x == 0) => {x: null, y: 33}
 *     noNil({x: null, y: 33, z: 0}, void 0) => {x: null, y: 33, z: 0}
 *     noNil(null) => {}
 *     noNil(false) => {}
 *
 * @param {Object} paramMap - the parameter object with parameter names keyed to the parameter values
 * @param {Function} isParameterValueEmpty - a function that returns true if given an empty parameter value.
 *                                           Defaults to lodash.isNil if not supplied.
 * @return {Object} parameter map with values matching isParameterValueEmpty removed
 */
export function noNil(paramMap = {}, isParameterValueEmpty = isNil) {
  paramMap = paramMap || {};

  if (!isParameterValueEmpty) {
    return paramMap;
  }

  const filteredParamMap = {};
  for (const param in paramMap) {
    if (paramMap.hasOwnProperty(param) && !isParameterValueEmpty(paramMap[param])) {
      filteredParamMap[param] = paramMap[param];
    }
  }
  return filteredParamMap;
}

/**
 * Returns a URL query string of the form a=b&c=d given a map of
 * query parameters.
 *
 * @param {Object} paramMap - an object of query parameters to be serialized into a query string
 * @param {boolean} allowNulls - if true, empty parameter values are accepted; otherwise null
 *                               and undefined parameter values will be discarded
 * @return {string} a URL query string of the form "a=b&c=d"
 */
export function urlParams(paramMap, allowNulls = true) {
  const searchParams = new URLSearchParams();
  for (let paramName in noNil(paramMap, allowNulls ? null : isNil)) {
    if (paramMap.hasOwnProperty(paramName)) {
      searchParams.append(paramName, paramMap[paramName]);
    }
  }
  return searchParams.toString();
}

/**
 * Joins [urlString] to the given query [paramMap] formatted as a query string using
 * [urlParams].
 *
 * Examples:
 *
 *     joinURL("/students", { name: "Emma Mack" }) => "/students?name=Emma+Mack
 *     joinURL("/students", {}) => "/students"
 *     joinURL("/students, null) => "/students"
 *
 * @param {string} urlString - the base URL that may need a query string suffixed
 * @param {Object} paramMap - an object of query parameters to be appended as a query string
 * @return {string} a URL with a query string if paramMap isn't empty
 */
export function joinURL(urlString, paramMap = {}) {
  const paramString = urlParams(paramMap);
  return paramString === "" ? urlString : `${urlString}?${paramString}`;
}

/**
 * Returns the given [fragment] URL prefixed with "#", or the empty
 * string if [fragment] is falsy.
 *
 * @param {string} fragment - a URL fragment, such as "/route/32"
 * @returns {string} the fragment URL prefixed with "#", such as "#/route/32"
 */
export const urlFragment = (fragment) => !fragment ? "" : `#${fragment}`;

const responseErrorMessage = (response) => {
  const errorInstructions =
    response.errorId ? ` ${SFConfig.errorInstructions} ${response.errorId} at time: ${moment().valueOf()}` : "";
  return response.message + errorInstructions;
};

const defaultUserMessage = "Sorry, we are having trouble processing your request. Please contact support.";

export const apiErrorMessage = error => {
  // This shouldn't really happen, it's only a fallback to avoid breaking stuff
  if ( typeof error === "undefined" ) return "There was an error.";
  if ( typeof error.json === "undefined" ) return error.message ? error.message : "There was an error.";
  const moreInfo = error.json.traceId
    ? ` Please notify support with the following information: ${error.json.traceId}`
    : '';
  return `${error.json.userMessage || defaultUserMessage}${moreInfo}`;
};

// requireSuccess throws an Error if the given response JSON indicates a server failure.
// If used in a Promise.then, this will return a rejected promise for server errors.
const requireSuccess = (response) => {
  if (!response.success) {
    throw new Error(responseErrorMessage(response));
  }

  return response;
};

/**
 * Generates a friendly, user-facing error for a throttled API request, including the number of seconds until they
 * can successfully retry.
 * @param {number} retryAfterSeconds - number of seconds until throttling is cleared, commonly provided by API headers
 * @return {string} the error message
 */
const throttleErrorMessage = retryAfterSeconds =>
  `We need time to process your last submission, please try again in ${retryAfterSeconds || 'a few'} 
  ${retryAfterSeconds === 1 ? 'second' : 'seconds'}.`

export const checkStatus = response => {
  return new Promise((resolve, reject) => {
    if ( response.status >= 200 && response.status < 300 ) return resolve(response);
    const error = new Error(response.statusText);
    const defaultJsonError = { status: "BAD REQUEST", traceId: null, userMessage: defaultUserMessage };
    return response.json()
      .then(responseJson => {
        // Receive JSON information about the error from the server
        error.json = Object.assign(
            {},
            response.status === 429 ?
                { status: "THROTTLED REQUEST", traceId: null, userMessage: throttleErrorMessage(responseJson.retryAfterSeconds) }
                : defaultJsonError,
            responseJson);
      })
      .catch(() => {
        // Default error attributes, means server hasn't provided extra information
        error.json = defaultJsonError;
      })
      .then(() => reject(error));
  });
}

// documentRoot takes the name of the root property of a JSON response and returns a
// function that takes a JSON response and returns the value of the root property.
// if root is undefined, it will return the response as is
const documentRoot = (root) => (response) => {
  if (!root) { return response }
  return response[root]
};

export const fetchAuthenticatedJSON = (url, options) =>
  fetchAuthenticated(url, options).then(checkStatus).then(responseJSON);

export const fetchAuthenticatedStarfishJSON = (url, root, options) =>
  fetchAuthenticated(url, options).then(responseJSON).then(requireSuccess).then(documentRoot(root));

export default {
  fetchAuthenticatedJSON, fetchAuthenticated, withCredentials, urlParams, fetchAuthenticatedStarfishJSON
};
